import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import LegalLayout from '../components/layout/LegalLayout';
import './ContactPage.css';

const apiBase = import.meta.env.DEV
  ? '' // use Vite proxy in dev
  : (import.meta.env.VITE_API_URL || ''); // Use relative URLs in production if VITE_API_URL not set

export default function ContactPage() {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email || !form.name || !form.message) {
      setError(t('contact.form.errorRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (!res.ok) {
        throw new Error(t('contact.form.errorGeneric'));
      }

      setSubmitted(true);
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Failed to submit contact form:', err);
      setError(err.message || t('contact.form.errorFallback'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout
      title={t('contact.title')}
      description={t('contact.description')}
    >
      <div className="contactPage">
        <h1 className="contactPage__title">{t('contact.title')}</h1>
        <p className="contactPage__subtitle">{t('contact.subtitle')}</p>

        <div className="contactPage__content">
          <div className="contactPage__info">
            <div className="contactPage__infoItem">
              <div className="contactPage__infoIcon">
                <Mail size={24} />
              </div>
              <div className="contactPage__infoContent">
                <h3 className="contactPage__infoTitle">{t('contact.email.title')}</h3>
                <a href="mailto:support@bodai.io" className="contactPage__emailLink">
                  support@bodai.io
                </a>
              </div>
            </div>
          </div>

          {submitted ? (
            <div className="contactPage__success">
              <div className="contactPage__successIcon">
                <CheckCircle size={48} strokeWidth={2.5} />
              </div>
              <h2 className="contactPage__successTitle">{t('contact.success.title')}</h2>
              <p className="contactPage__successText">{t('contact.success.message')}</p>
            </div>
          ) : (
            <form className="contactPage__form" onSubmit={handleSubmit}>
              <div className="contactPage__field">
                <label htmlFor="name" className="contactPage__label">
                  {t('contact.form.nameLabel')}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t('contact.form.namePlaceholder')}
                  required
                  className="contactPage__input"
                />
              </div>

              <div className="contactPage__field">
                <label htmlFor="email" className="contactPage__label">
                  {t('contact.form.emailLabel')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder={t('contact.form.emailPlaceholder')}
                  required
                  className="contactPage__input"
                />
              </div>

              <div className="contactPage__field">
                <label htmlFor="message" className="contactPage__label">
                  {t('contact.form.messageLabel')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.messagePlaceholder')}
                  required
                  className="contactPage__textarea"
                />
              </div>

              {error && (
                <div className="contactPage__error">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="contactPage__submit"
                disabled={submitting}
              >
                <span>{submitting ? t('contact.form.submitting') : t('contact.form.submit')}</span>
                {!submitting && <Send size={18} className="contactPage__submitIcon" />}
              </button>
            </form>
          )}
        </div>
      </div>
    </LegalLayout>
  );
}


